import {
  Alert,
  Button,
  FormRow,
  Input,
  Modal,
  Spinner,
  Stack
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  DestinationConfig,
  useOrganizationForwardingPost,
  useOrganizationForwardingPut
} from '../api/hooks/useOrganizationForwarding';

interface CreateAndUpdateFitCoDestinationForm {
  destinationId: string;
  publicServiceIdenitifier: string;
  name: string;
}

interface CreateAndUpdateMandantCredsModalProps {
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
  readonly isOpen: boolean;
  readonly orgUnitId: string;
  readonly destinationId?: string;
  readonly publicServiceIdentifier?: string;
  readonly publicServiceId: string;
  readonly forwardingName?: string;
}

export function CreateAndUpdateFitCoDestinationModal(
  props: CreateAndUpdateMandantCredsModalProps
) {
  const {
    isOpen,
    onClose,
    onSuccess,
    orgUnitId,
    publicServiceId,
    publicServiceIdentifier,
    destinationId,
    forwardingName
  } = props;
  const isUpdatingDestination = !!publicServiceIdentifier;
  const { t } = useTranslation();
  const { mutateAsync: updateOrganizationForwarding, isPending: isUpdating } =
    useOrganizationForwardingPut();
  const { mutateAsync: createOrganizationForwarding, isPending: isCreating } =
    useOrganizationForwardingPost();

  const {
    formState: { isValid },
    register,
    handleSubmit
  } = useForm<CreateAndUpdateFitCoDestinationForm>({
    mode: 'onChange',
    defaultValues: {
      destinationId: destinationId ?? '',
      publicServiceIdenitifier: publicServiceIdentifier ?? '',
      name: forwardingName ?? ''
    },
    resolver: joiResolver(
      Joi.object<CreateAndUpdateFitCoDestinationForm>({
        destinationId: Joi.string().guid().required(),
        publicServiceIdenitifier: Joi.string().required(),
        name: Joi.string().allow('')
      })
    )
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="CreateAndUpdateFitCoDestinationModal-header">
        {t('createAndUpdateFitCoDestModal.title')}
      </Modal.Header>

      <Modal.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Stack spacing="xl" direction="column">
            <div className="descriptionText">
              {t('createAndUpdateFitCoDestModal.subline')}
            </div>
            <Alert
              variant={'info'}
              message={t('createAndUpdateFitCoDestModal.subline.info')}
            />
            <Stack spacing="s">
              <FormRow
                label={t('createAndUpdateFitCoDestModal.destinationId.label')}
              >
                <Input
                  id="destinationId"
                  readonly={isUpdatingDestination}
                  type="text"
                  {...register('destinationId')}
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.destinationId.placeholder'
                  )}
                />
              </FormRow>
              <FormRow
                label={t('createAndUpdateFitCoDestModal.publicServiceId.label')}
              >
                <Input
                  id="publicServiceIdentifier"
                  readonly={isUpdatingDestination}
                  type="text"
                  {...register('publicServiceIdenitifier')}
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.publicServiceId.placeholder'
                  )}
                />
              </FormRow>
              <FormRow
                label={t('createAndUpdateFitCoDestModal.zustellpunkt.label')}
              >
                <Input
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.zustellpunkt.placeholder'
                  )}
                  id="name"
                  {...register('name')}
                  type="text"
                />
              </FormRow>
            </Stack>
            {
              <Button
                variant="primary"
                disabled={!isValid || isCreating || isUpdating}
                onClick={handleSubmit((state) => {
                  const destinationConfig: DestinationConfig = {
                    name: state.name,
                    publicServiceIdentifier: publicServiceId,
                    forwardingType: 'FITCONNECT',
                    forwardingTargetConfig: {
                      destinationId: state.destinationId,
                      publicServiceIdentifier: state.publicServiceIdenitifier
                    }
                  };
                  if (destinationId) {
                    updateOrganizationForwarding({
                      orgUnitId,
                      destinationConfig
                    }).catch(() => {});
                  } else {
                    createOrganizationForwarding({
                      orgUnitId,
                      destinationConfig
                    }).catch(() => {});
                  }
                })}
              >
                {isCreating || isUpdating ? (
                  <Spinner />
                ) : (
                  t('createAndUpdateFitCoDestModal.connect.button')
                )}
              </Button>
            }
          </Stack>
        </form>
      </Modal.Content>
      <Modal.Footer direction="rtl" alignment="none">
        <Button size="sm" variant="secondary-outline" onClick={onClose}>
          {t('globalNotifications.modalAboard')}
        </Button>
        <Button size="sm" variant="primary" onClick={onSuccess}>
          {t('globalNotifications.modalSave')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
